import { useSimulationStore } from '../../stores/simulationStore';

// Data state types for clear display semantics
type DataState =
  | { type: 'value'; value: number }
  | { type: 'loading' }      // Waiting for first data
  | { type: 'unavailable' }  // Sensor not present
  | { type: 'error' }        // Sensor failure
  | { type: 'null' };        // Valid null reading

function getDataState(value: number | null | undefined, isConnected: boolean = true): DataState {
  if (!isConnected) return { type: 'loading' };
  if (value === undefined) return { type: 'loading' };
  if (value === null) return { type: 'null' };
  return { type: 'value', value };
}

// Format value with unit for inline displays
function formatValue(
  value: number | null | undefined,
  unit: string,
  precision: number = 1
): { text: string; className: string } {
  if (value === undefined) return { text: '···', className: 'text-gray-600' };
  if (value === null) return { text: '—', className: 'text-gray-500' };
  return { text: `${value.toFixed(precision)} ${unit}`, className: 'text-gray-100' };
}

// Data card component with semantic state display
function DataCard({
  label,
  value,
  unit,
  precision = 1,
  state,
}: {
  label: string;
  value?: number | null | undefined;
  unit: string;
  precision?: number;
  state?: DataState;
}) {
  // Derive state from value if not explicitly provided
  const dataState = state ?? getDataState(value);

  let displayValue: string;
  let valueClass = 'data-value';

  switch (dataState.type) {
    case 'value':
      displayValue = dataState.value.toFixed(precision);
      break;
    case 'loading':
      displayValue = '···';
      valueClass = 'data-value text-gray-600';
      break;
    case 'unavailable':
      displayValue = 'N/A';
      valueClass = 'data-value text-gray-600 text-sm';
      break;
    case 'error':
      displayValue = 'ERR';
      valueClass = 'data-value text-red-500 text-sm';
      break;
    case 'null':
      displayValue = '—';
      valueClass = 'data-value text-gray-500';
      break;
  }

  return (
    <div className="data-card">
      <div className="data-label">{label}</div>
      <div className="flex items-baseline">
        <span className={valueClass}>{displayValue}</span>
        {dataState.type === 'value' && <span className="data-unit">{unit}</span>}
      </div>
    </div>
  );
}

// Position section
function PositionSection() {
  const { state } = useSimulationStore();
  const vessel = state?.vessel;

  const formatCoord = (value: number | undefined, isLat: boolean) => {
    if (value === undefined) return '···';
    const abs = Math.abs(value);
    const deg = Math.floor(abs);
    const min = (abs - deg) * 60;
    const dir = isLat ? (value >= 0 ? 'N' : 'S') : value >= 0 ? 'E' : 'W';
    return `${deg}° ${min.toFixed(3)}' ${dir}`;
  };

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Position
      </h3>
      <div className="data-card">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-gray-500">LAT</span>
            <div className="font-mono text-gray-100">
              {formatCoord(vessel?.latitude, true)}
            </div>
          </div>
          <div>
            <span className="text-gray-500">LON</span>
            <div className="font-mono text-gray-100">
              {formatCoord(vessel?.longitude, false)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Speed section
function SpeedSection() {
  const { state } = useSimulationStore();
  const vessel = state?.vessel;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Speed
      </h3>
      <div className="grid grid-cols-2 gap-2">
        <DataCard label="SOG" value={vessel?.speed_over_ground} unit="kn" />
        <DataCard label="STW" value={vessel?.speed_through_water} unit="kn" />
      </div>
    </div>
  );
}

// Heading section
function HeadingSection() {
  const { state } = useSimulationStore();
  const vessel = state?.vessel;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Heading
      </h3>
      <div className="grid grid-cols-2 gap-2">
        <DataCard label="HDG" value={vessel?.heading} unit="°" precision={0} />
        <DataCard label="COG" value={vessel?.course_over_ground} unit="°" precision={0} />
      </div>
    </div>
  );
}

// Wind section
function WindSection() {
  const { state } = useSimulationStore();
  const wind = state?.wind;
  const nmea = state?.nmea_data;

  if (!wind && state?.vessel?.config.vessel_type !== 'sailboat') {
    return null;
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Wind
      </h3>
      <div className="data-card space-y-3">
        <div>
          <div className="text-xs text-gray-500 mb-1">TRUE</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-xs text-gray-500">TWS</span>
              <div className={`font-mono ${formatValue(wind?.true_wind_speed ?? nmea?.wind_speed_true, 'kn', 1).className}`}>
                {formatValue(wind?.true_wind_speed ?? nmea?.wind_speed_true, 'kn', 1).text}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">TWD</span>
              <div className={`font-mono ${formatValue(wind?.true_wind_direction ?? nmea?.wind_direction_true, '°', 0).className}`}>
                {formatValue(wind?.true_wind_direction ?? nmea?.wind_direction_true, '°', 0).text}
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">APPARENT</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-xs text-gray-500">AWS</span>
              <div className={`font-mono ${formatValue(wind?.apparent_wind_speed ?? nmea?.wind_speed_apparent, 'kn', 1).className}`}>
                {formatValue(wind?.apparent_wind_speed ?? nmea?.wind_speed_apparent, 'kn', 1).text}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">AWA</span>
              <div className={`font-mono ${formatValue(wind?.apparent_wind_angle ?? nmea?.wind_angle_apparent, '°', 0).className}`}>
                {formatValue(wind?.apparent_wind_angle ?? nmea?.wind_angle_apparent, '°', 0).text}
              </div>
            </div>
          </div>
        </div>
        {wind?.point_of_sail && (
          <div className="pt-2 border-t border-nautical-border">
            <span className="text-xs text-gray-500">Point of Sail</span>
            <div className="text-marine-400 font-medium">
              {formatPointOfSail(wind.point_of_sail)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function formatPointOfSail(pos: string): string {
  const names: Record<string, string> = {
    InIrons: 'In Irons',
    CloseHauled: 'Close Hauled',
    CloseReach: 'Close Reach',
    BeamReach: 'Beam Reach',
    BroadReach: 'Broad Reach',
    Running: 'Running',
  };
  return names[pos] || pos;
}

// Depth section
function DepthSection() {
  const { state } = useSimulationStore();
  const vessel = state?.vessel;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Depth
      </h3>
      <div className="grid grid-cols-3 gap-2">
        <DataCard label="DBT" value={vessel?.depth_below_transducer} unit="m" />
        <DataCard label="DBS" value={vessel?.depth_below_surface} unit="m" />
        <DataCard label="DBK" value={vessel?.depth_below_keel} unit="m" />
      </div>
    </div>
  );
}

// Environment section
function EnvironmentSection() {
  const { state } = useSimulationStore();
  const nmea = state?.nmea_data;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Environment
      </h3>
      <div className="grid grid-cols-3 gap-2">
        <DataCard label="Water" value={nmea?.water_temperature} unit="°C" />
        <DataCard label="Air" value={nmea?.air_temperature} unit="°C" />
        <DataCard label="Baro" value={nmea?.barometric_pressure} unit="mb" precision={0} />
      </div>
    </div>
  );
}

// Attitude section (for sailboats)
function AttitudeSection() {
  const { state } = useSimulationStore();
  const vessel = state?.vessel;

  if (vessel?.config.vessel_type !== 'sailboat') {
    return null;
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Attitude
      </h3>
      <div className="grid grid-cols-3 gap-2">
        <DataCard label="Heel" value={vessel?.roll} unit="°" />
        <DataCard label="Pitch" value={vessel?.pitch} unit="°" />
        <DataCard label="Leeway" value={vessel?.leeway} unit="°" />
      </div>
    </div>
  );
}

// Progress section
function ProgressSection() {
  const { state, route } = useSimulationStore();
  const routeState = state?.route_state;

  if (!route) return null;

  // Use route_state for accurate multi-waypoint progress
  const distanceTraveled = routeState?.distance_traveled ?? 0;
  const distanceRemaining = routeState?.distance_remaining ?? route.distance_nm;
  const progressPercent = routeState?.progress_percent ?? 0;
  const totalDistance = routeState?.total_distance_nm ?? route.distance_nm;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Progress
      </h3>
      <div className="data-card">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-400">
            {distanceTraveled.toFixed(2)} nm
          </span>
          <span className="text-gray-400">
            {distanceRemaining.toFixed(2)} nm
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          />
        </div>
        <div className="text-center text-xs text-gray-500 mt-1">
          {progressPercent.toFixed(1)}% of {totalDistance.toFixed(1)} nm
        </div>
        {routeState && routeState.total_legs > 1 && (
          <div className="text-center text-xs text-gray-500 mt-1">
            Leg {routeState.current_leg_index + 1} / {routeState.total_legs}
          </div>
        )}
      </div>
    </div>
  );
}

// Electrical section
function ElectricalSection() {
  const { state } = useSimulationStore();
  const nmea = state?.nmea_data;

  const getBatteryColor = (voltage: number | undefined) => {
    if (!voltage) return 'text-gray-400';
    if (voltage >= 12.6) return 'text-green-400';
    if (voltage >= 12.2) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getSOCColor = (soc: number | undefined) => {
    if (!soc) return 'text-gray-400';
    if (soc >= 80) return 'text-green-400';
    if (soc >= 50) return 'text-yellow-400';
    if (soc >= 20) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Electrical
      </h3>
      <div className="data-card space-y-3">
        {/* House Battery */}
        <div>
          <div className="text-xs text-gray-500 mb-1">HOUSE BATTERY</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-xs text-gray-500">Voltage</span>
              <div className={`font-mono ${nmea?.battery1_voltage !== undefined ? getBatteryColor(nmea.battery1_voltage) : 'text-gray-600'}`}>
                {nmea?.battery1_voltage !== undefined ? `${nmea.battery1_voltage.toFixed(2)} V` : '···'}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">Current</span>
              <div className={`font-mono ${nmea?.battery1_current !== undefined ? ((nmea.battery1_current) >= 0 ? 'text-green-400' : 'text-orange-400') : 'text-gray-600'}`}>
                {nmea?.battery1_current !== undefined ? `${nmea.battery1_current.toFixed(1)} A` : '···'}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">SOC</span>
              <div className={`font-mono ${nmea?.battery1_soc !== undefined ? getSOCColor(nmea.battery1_soc) : 'text-gray-600'}`}>
                {nmea?.battery1_soc !== undefined ? `${nmea.battery1_soc.toFixed(0)}%` : '···'}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">Temp</span>
              <div className={`font-mono ${nmea?.battery1_temperature !== undefined ? 'text-gray-100' : 'text-gray-600'}`}>
                {nmea?.battery1_temperature !== undefined ? `${nmea.battery1_temperature.toFixed(1)}°C` : '···'}
              </div>
            </div>
          </div>
        </div>

        {/* Start Battery */}
        <div className="pt-2 border-t border-nautical-border">
          <div className="text-xs text-gray-500 mb-1">START BATTERY</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-xs text-gray-500">Voltage</span>
              <div className={`font-mono ${nmea?.battery2_voltage !== undefined ? getBatteryColor(nmea.battery2_voltage) : 'text-gray-600'}`}>
                {nmea?.battery2_voltage !== undefined ? `${nmea.battery2_voltage.toFixed(2)} V` : '···'}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">SOC</span>
              <div className={`font-mono ${nmea?.battery2_soc !== undefined ? getSOCColor(nmea.battery2_soc) : 'text-gray-600'}`}>
                {nmea?.battery2_soc !== undefined ? `${nmea.battery2_soc.toFixed(0)}%` : '···'}
              </div>
            </div>
          </div>
        </div>

        {/* Charging */}
        <div className="pt-2 border-t border-nautical-border">
          <div className="text-xs text-gray-500 mb-1">CHARGING</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-xs text-gray-500">Solar</span>
              <div className={`font-mono ${nmea?.solar_output !== undefined ? 'text-yellow-400' : 'text-gray-600'}`}>
                {nmea?.solar_output !== undefined ? `${nmea.solar_output.toFixed(0)} W` : '···'}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">Alternator</span>
              <div className={`font-mono ${nmea?.alternator_output !== undefined ? 'text-blue-400' : 'text-gray-600'}`}>
                {nmea?.alternator_output !== undefined ? `${nmea.alternator_output.toFixed(1)} A` : '···'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Engine section
function EngineSection() {
  const { state } = useSimulationStore();
  const nmea = state?.nmea_data;
  const vessel = state?.vessel;

  // Hide for sailboats unless engine is running
  if (vessel?.config.vessel_type === 'sailboat' && (nmea?.engine_rpm ?? 0) < 100) {
    return null;
  }

  const isRunning = (nmea?.engine_rpm ?? 0) > 100;

  const getTempColor = (temp: number | undefined, warn: number, critical: number) => {
    if (!temp) return 'text-gray-400';
    if (temp >= critical) return 'text-red-400';
    if (temp >= warn) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getPressureColor = (pressure: number | undefined) => {
    if (!pressure || pressure < 150) return 'text-red-400';
    if (pressure < 200) return 'text-yellow-400';
    return 'text-green-400';
  };

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${isRunning ? 'bg-green-500' : 'bg-gray-600'}`} />
        Engine
      </h3>
      <div className="data-card space-y-3">
        {/* RPM and Load */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-xs text-gray-500">RPM</span>
            <div className="font-mono text-gray-100 text-lg">
              {nmea?.engine_rpm?.toFixed(0) ?? '0'}
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500">Load</span>
            <div className="font-mono text-gray-100">
              {nmea?.engine_load?.toFixed(0) ?? '0'}%
            </div>
          </div>
        </div>

        {/* Temperatures */}
        <div className="pt-2 border-t border-nautical-border">
          <div className="text-xs text-gray-500 mb-1">TEMPERATURES</div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <span className="text-xs text-gray-500">Coolant</span>
              <div className={`font-mono ${nmea?.engine_coolant_temp !== undefined ? getTempColor(nmea.engine_coolant_temp, 95, 105) : 'text-gray-600'}`}>
                {nmea?.engine_coolant_temp !== undefined ? `${nmea.engine_coolant_temp.toFixed(0)}°C` : '···'}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">Oil</span>
              <div className={`font-mono ${nmea?.engine_oil_temp !== undefined ? getTempColor(nmea.engine_oil_temp, 100, 120) : 'text-gray-600'}`}>
                {nmea?.engine_oil_temp !== undefined ? `${nmea.engine_oil_temp.toFixed(0)}°C` : '···'}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">Exhaust</span>
              <div className={`font-mono ${nmea?.engine_exhaust_temp !== undefined ? getTempColor(nmea.engine_exhaust_temp, 450, 550) : 'text-gray-600'}`}>
                {nmea?.engine_exhaust_temp !== undefined ? `${nmea.engine_exhaust_temp.toFixed(0)}°C` : '···'}
              </div>
            </div>
          </div>
        </div>

        {/* Oil Pressure and Hours */}
        <div className="pt-2 border-t border-nautical-border">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-xs text-gray-500">Oil Pressure</span>
              <div className={`font-mono ${nmea?.engine_oil_pressure !== undefined ? (isRunning ? getPressureColor(nmea.engine_oil_pressure) : 'text-gray-400') : 'text-gray-600'}`}>
                {nmea?.engine_oil_pressure !== undefined ? `${nmea.engine_oil_pressure.toFixed(0)} kPa` : '···'}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">Hours</span>
              <div className="font-mono text-gray-100">
                {nmea?.engine_hours?.toFixed(1) ?? '0'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Fuel section
function FuelSection() {
  const { state } = useSimulationStore();
  const nmea = state?.nmea_data;
  const vessel = state?.vessel;

  // Only show for powerboats
  if (vessel?.config.vessel_type !== 'powerboat') {
    return null;
  }

  const getFuelColor = (level: number | undefined) => {
    if (!level) return 'text-gray-400';
    if (level >= 50) return 'text-green-400';
    if (level >= 25) return 'text-yellow-400';
    if (level >= 10) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Fuel
      </h3>
      <div className="data-card">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <span className="text-xs text-gray-500">Level</span>
            <div className={`font-mono ${nmea?.fuel_level !== undefined ? getFuelColor(nmea.fuel_level) : 'text-gray-600'}`}>
              {nmea?.fuel_level !== undefined ? `${nmea.fuel_level.toFixed(0)}%` : '···'}
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500">Rate</span>
            <div className="font-mono text-gray-100">
              {nmea?.fuel_rate?.toFixed(1) ?? '0'} L/h
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-500">Pressure</span>
            <div className="font-mono text-gray-100">
              {nmea?.fuel_pressure?.toFixed(0) ?? '0'} kPa
            </div>
          </div>
        </div>
        {/* Fuel level bar */}
        <div className="mt-2">
          <div className="h-2 bg-nautical-darker rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                (nmea?.fuel_level ?? 0) >= 50 ? 'bg-green-500' :
                (nmea?.fuel_level ?? 0) >= 25 ? 'bg-yellow-500' :
                (nmea?.fuel_level ?? 0) >= 10 ? 'bg-orange-500' : 'bg-red-500'
              }`}
              style={{ width: `${nmea?.fuel_level ?? 0}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Fault warning banner
function FaultWarningBanner() {
  const { activeFaultCount } = useSimulationStore();

  if (activeFaultCount === 0) return null;

  return (
    <div className="bg-red-950/60 border border-red-500/40 rounded-lg px-3 py-2 text-center">
      <span className="text-red-400 text-sm font-medium">
        {activeFaultCount} Fault{activeFaultCount !== 1 ? 's' : ''} Active
      </span>
      <span className="text-red-500/70 text-xs block">
        Data shown may reflect simulated failures
      </span>
    </div>
  );
}

// Main dashboard
export default function Dashboard() {
  return (
    <div className="p-4 space-y-4">
      <FaultWarningBanner />
      <PositionSection />
      <SpeedSection />
      <HeadingSection />
      <WindSection />
      <DepthSection />
      <EnvironmentSection />
      <AttitudeSection />
      <EngineSection />
      <FuelSection />
      <ElectricalSection />
      <ProgressSection />
    </div>
  );
}
